// =============================================================================
// FILE: packages/shared/src/config/brand.config.ts
// LAYER: Foundation — RCD2 Product Family branding
// PURPOSE: Single source of truth for product identity (name, short name,
//          tag-line, monogram, primary accents, logo glyph path). Every
//          UI surface (TopBar, Sidebar, LoginPage, favicon, document title)
//          reads from this config so we can ship multiple RCD2 products
//          (BIM OPS, BIM OPS License Server, RCD2 Engine core) from one
//          codebase without per-fork hardcoded asset edits.
// =============================================================================

export type RCD2ProductId =
  | 'bim-ops'
  | 'bim-ops-license-server'
  | 'rcd2-engine';

export interface BrandConfig {
  /** Stable identifier — never localise. */
  readonly id: RCD2ProductId;
  /** Family root — shown in footers and About boxes. */
  readonly family: 'RCD2';
  /** Full product name shown in headers and OS chrome. */
  readonly productName: string;
  /** Short name for compact UI (sidebar collapse, tab). */
  readonly shortName: string;
  /** Marketing tagline / sub-title. */
  readonly tagline: string;
  /** Monogram shown in the corner badge instead of a raster logo. */
  readonly monogram: string;
  /** Primary accent — used by buttons, monogram, focus rings. */
  readonly accent: string;
  /** Secondary accent — used by highlights, hover, gradients. */
  readonly accentSecondary: string;
  /** Window title / document.title prefix. */
  readonly windowTitle: string;
  /** Optional favicon override (data URI or relative path). */
  readonly favicon: string | null;
  /** Product version string. */
  readonly version?: string;
  /** Copyright line. */
  readonly copyright: string;
  /** Creator credits (used by Footer, About dialog, Splash). */
  readonly creator: {
    readonly name: string;
    readonly credential: string;
    readonly role: string;
    readonly location: string;
  };
}

// ---------------------------------------------------------------------------
// Product catalogue — every RCD2 product registered here.
// ---------------------------------------------------------------------------
export const PRODUCTS: Readonly<Record<RCD2ProductId, BrandConfig>> = {
  'bim-ops': {
    id: 'bim-ops',
    family: 'RCD2',
    productName: 'BIM OPS',
    shortName: 'BIM OPS',
    tagline: 'Telecommunications BIM Operations Platform',
    // Engineering mark — hexagon glyph + JB monogram. Rendered as SVG by
    // the LogoMark component; this string is used as a textual fallback
    // for the tab-collapse pill and ASCII contexts (logs, splash boot lines).
    monogram: 'JB',
    accent: '#fcd34d',          // amber-300 — zinc/amber theme
    accentSecondary: '#60a5fa', // blue-400
    windowTitle: 'BIM OPS',
    favicon: null,
    version: '1.0.0',
    copyright: '© 2026 John Bismonte, RCDD',
    creator: {
      name: 'John Bismonte, RCDD',
      credential: 'RCDD',
      role: 'Telecommunications Infrastructure Designer',
      location: 'Calgary, Alberta, Canada',
    },
  },
  'bim-ops-license-server': {
    id: 'bim-ops-license-server',
    family: 'RCD2',
    productName: 'BIM OPS License Server',
    shortName: 'License',
    tagline: 'Centralised feature gating + entitlement for the RCD2 product family.',
    monogram: 'JB',
    accent: '#a78bfa',
    accentSecondary: '#22d3ee',
    windowTitle: 'BIM OPS · License',
    favicon: null,
    copyright: '© 2026 John Bismonte, RCDD',
    creator: {
      name: 'John Bismonte, RCDD',
      credential: 'RCDD',
      role: 'Telecommunications Infrastructure Designer',
      location: 'Calgary, Alberta, Canada',
    },
  },
  'rcd2-engine': {
    id: 'rcd2-engine',
    family: 'RCD2',
    productName: 'BIM OPS Engine',
    shortName: 'Engine',
    tagline: 'Offline-first BIM data engine. Embeds in any RCD2 surface.',
    monogram: 'JB',
    accent: '#34d399',
    accentSecondary: '#fcd34d',
    windowTitle: 'BIM OPS · Engine',
    favicon: null,
    copyright: '© 2026 John Bismonte, RCDD',
    creator: {
      name: 'John Bismonte, RCDD',
      credential: 'RCDD',
      role: 'Telecommunications Infrastructure Designer',
      location: 'Calgary, Alberta, Canada',
    },
  },
};

// ---------------------------------------------------------------------------
// Active product — keep this single line as the only place that needs to
// change when re-skinning the codebase for a sibling RCD2 product.
// ---------------------------------------------------------------------------
export const ACTIVE_PRODUCT: RCD2ProductId = 'bim-ops';

export function getBrand(id: RCD2ProductId = ACTIVE_PRODUCT): BrandConfig {
  return PRODUCTS[id];
}
